// Expands `@for` blocks inside object-variable bodies into flat key/value text.
// Keeps unsupported control syntax untouched while reusing loop placeholder rules.
import type { ForLoopExpansionOptions, LoopScope } from '../../loops/for-loop/types'
import { countLineAt, handleLoopLimitViolation } from '../../loops/for-loop/expand-helpers'
import { findNextForLoopStart, parseForLoop, resolveLoopValues } from '../../loops/for-loop/parser'
import { replaceLoopPlaceholders } from '../../loops/for-loop/placeholders'
import { createLoopIterationScope } from '../../loops/for-loop/scope'

export type ObjectVariableBodyForOptions = ForLoopExpansionOptions & {
  lineOffset?: number
}

// Expand only `@for(...) { ... }` blocks inside one object-variable body.
export function expandObjectVariableBodyForLoops(
  source: string,
  options: ObjectVariableBodyForOptions,
): string {
  // Return the original body when no loop token is present.
  if (!source.includes('for')) {
    return source
  } else {
    // Continue recursive object-body loop expansion.
  }

  // Start recursive expansion from an empty loop scope.
  return expandObjectVariableBodyForLoopsInternal(source, options, {}, 1)
}

// Expand one object-variable body segment with the current loop scope.
function expandObjectVariableBodyForLoopsInternal(
  source: string,
  options: ObjectVariableBodyForOptions,
  scope: LoopScope,
  activeLoopProduct: number,
): string {
  let cursor = 0
  let output = ''

  // Scan and expand each object-body loop block in source order.
  while (cursor < source.length) {
    const nextLoopStart = findNextForLoopStart(source, cursor)
    // Flush the remaining plain text when no more loops are found.
    if (nextLoopStart === -1) {
      output += resolveObjectBodyPlaceholders(source.slice(cursor), scope, options)
      break
    } else {
      // Continue object-body loop parsing.
    }

    output += resolveObjectBodyPlaceholders(source.slice(cursor, nextLoopStart), scope, options)
    const loop = parseForLoop(source, nextLoopStart)
    // Keep raw text when scanner cannot parse a valid loop block here.
    if (!loop) {
      output += source[nextLoopStart]
      cursor = nextLoopStart + 1
      continue
    } else {
      // Continue iterator-source resolution.
    }

    const resolvedSource = replaceLoopPlaceholders(
      loop.sourceExpression,
      scope,
      options.arrays,
      options.variables,
      options.objectVariables,
    )
    const values = resolveLoopValues(resolvedSource, options.arrays, options.objectVariables)
    // Preserve the raw loop block when the iterator source cannot resolve.
    if (!values) {
      output += source.slice(nextLoopStart, loop.endIndex)
      cursor = loop.endIndex
      continue
    } else {
      // Continue budget checks and body emission.
    }

    const loopLine = resolveLoopLine(source, nextLoopStart, options.lineOffset)
    // Enforce per-loop iteration budget before emitting generated entries.
    if (values.length > options.resourceLimits.maxForIterationsPerLoop) {
      handleLoopLimitViolation(
        options,
        loopLine,
        `Loop iteration budget exceeded (${values.length}/${options.resourceLimits.maxForIterationsPerLoop}) in ${options.sourceId}.`,
      )
      cursor = loop.endIndex
      continue
    } else {
      // Continue nested-loop budget validation.
    }

    const nextLoopProduct = activeLoopProduct * values.length
    // Enforce nested loop cartesian-product budget inside object bodies.
    if (nextLoopProduct > options.resourceLimits.maxNestedLoopProduct) {
      handleLoopLimitViolation(
        options,
        loopLine,
        `Nested loop product exceeded (${nextLoopProduct}/${options.resourceLimits.maxNestedLoopProduct}) in ${options.sourceId}.`,
      )
      cursor = loop.endIndex
      continue
    } else {
      // Continue body expansion when budgets remain within limits.
    }

    // Emit one expanded object-entry segment per resolved loop value.
    for (let valueIndex = 0; valueIndex < values.length; valueIndex += 1) {
      const value = values[valueIndex]
      const nestedScope = createLoopIterationScope(
        scope,
        loop.variableName,
        value,
        valueIndex,
        loop.indexVariableName,
      )
      output += expandObjectVariableBodyForLoopsInternal(loop.body, options, nestedScope, nextLoopProduct)
    }

    cursor = loop.endIndex
  }

  // Return the expanded object-body source for the current scope.
  return output
}

// Resolve `${...}` placeholders only in plain object-body text segments.
function resolveObjectBodyPlaceholders(
  source: string,
  scope: LoopScope,
  options: ObjectVariableBodyForOptions,
): string {
  // Return source unchanged when no placeholder marker exists.
  if (!source.includes('$')) {
    return source
  } else {
    // Continue placeholder replacement for plain object-body text.
  }

  // Return the placeholder-resolved object-body text segment.
  return replaceLoopPlaceholders(source, scope, options.arrays, options.variables, options.objectVariables)
}

// Resolve the final warning line for one object-body loop start.
function resolveLoopLine(source: string, index: number, lineOffset = 1): number {
  // Return the declaration-relative line adjusted by the outer offset.
  return lineOffset + countLineAt(source, index) - 1
}
